import { useEffect, useRef, useState } from "react";
import { Copy, ImageUp, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, errMsg, copyOrWarn } from "@/lib/api";

export const Branding = () => {
  const [b, setB] = useState(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const apply = (data) => { setB(data); setName(data.name); setSlug(data.slug); };
  useEffect(() => { api.get("/settings/branding").then(({ data }) => apply(data)).catch((e) => toast.error(errMsg(e))); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { apply((await fn()).data); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = () => run(() => api.put("/settings/branding", { name, slug }), "Personnalisation enregistrée");
  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    run(() => api.post("/settings/branding/logo", fd), "Logo mis à jour");
  };
  const removeLogo = () => run(() => api.delete("/settings/branding/logo"), "Logo retiré");

  if (!b) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const link = `${window.location.origin}/join/${b.slug}`;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-branding">
      <div className="rounded-lg border border-slate-200 p-6 space-y-5">
        <div>
          <h3 className="font-semibold mb-1">Logo</h3>
          <p className="text-sm text-slate-500 mb-4">Affiché en haut à gauche de la page où vos clients saisissent leur code. PNG, JPG, SVG ou WEBP, 2 Mo maximum.</p>
          <div className="flex items-center gap-4">
            <div className="h-16 w-44 rounded-md border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden" data-testid="branding-logo-preview">
              {b.logo_version ? <img src={`${API}/public/brand/${b.slug}/logo?v=${b.logo_version}`} alt="Logo" className="max-h-12 max-w-[160px] object-contain" /> : <span className="text-xs text-slate-400">Aucun logo</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={upload} data-testid="branding-logo-input" />
            <Button variant="secondary" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload-btn"><ImageUp className="h-4 w-4 mr-2" /> Choisir une image</Button>
            {b.logo_version && <Button variant="ghost" disabled={busy} onClick={removeLogo} data-testid="branding-logo-remove-btn"><Trash2 className="h-4 w-4 mr-2" /> Retirer</Button>}
          </div>
        </div>
        <label className="block text-sm">
          <span className="text-slate-600">Nom affiché</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="mt-1" data-testid="branding-name-input" />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">Adresse de votre page client</span>
          <div className="mt-1 flex items-center rounded-md border border-input overflow-hidden">
            <span className="px-3 text-slate-500 bg-slate-50 h-9 grid place-items-center border-r border-input whitespace-nowrap">{window.location.host}/join/</span>
            <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="flex-1 h-9 px-3 outline-none text-sm" data-testid="branding-slug-input" />
          </div>
        </label>
        <Button onClick={save} disabled={busy || !name.trim() || !slug.trim()} data-testid="branding-save-btn">Enregistrer</Button>
      </div>
      <div className="rounded-lg border border-slate-200 p-6">
        <h3 className="font-semibold mb-1">Lien à donner à vos clients</h3>
        <p className="text-sm text-slate-500 mb-3">Votre logo et votre nom s'affichent dès l'ouverture de la page.</p>
        <div className="flex gap-2">
          <Input readOnly value={link} data-testid="branding-join-link" />
          <Button variant="secondary" onClick={() => copyOrWarn(link, "Lien copié")} data-testid="branding-copy-link-btn"><Copy className="h-4 w-4" /></Button>
          <Button variant="ghost" asChild><a href={link} target="_blank" rel="noreferrer" data-testid="branding-open-link">Voir</a></Button>
        </div>
      </div>
    </div>
  );
};
